import { useRef, useState } from 'react';
import { useAppDispatch } from '@src/state/hooks';
import { loadBox } from '@src/state/boxSlice';

/** A modal-local preview. Only the modal's close handler calls save. */
export function useRecipientFrameDraft(boxKey: string, giftId: number | undefined, initialFrame?: string | null, onSaved?: (frame: string | null) => void) {
  const dispatch = useAppDispatch();
  const [choice, setChoice] = useState({ frame: initialFrame || 'frame_2', enabled: Boolean(initialFrame) });
  const latest = useRef(choice);
  const saved = useRef(initialFrame || null);
  const pending = useRef<Promise<void> | null>(null);
  const select = (frame: string, enabled: boolean) => {
    latest.current = { frame, enabled };
    setChoice(latest.current);
  };
  const save = () => {
    if (pending.current) return pending.current;
    const frameName = latest.current.enabled ? latest.current.frame : null;
    if (!giftId || frameName === saved.current) return Promise.resolve();
    const request = (async () => {
      const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/gift/recipient-frame`, {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ giftId, frameName }),
      });
      if (!response.ok) throw new Error('Frame save failed');
      saved.current = frameName;
      onSaved?.(frameName);
      await dispatch(loadBox({ key: boxKey }));
    })();
    pending.current = request;
    return request.finally(() => { pending.current = null; });
  };
  return { ...choice, select, save };
}
export type RecipientFrameDraft = ReturnType<typeof useRecipientFrameDraft>;
